/**
 * One open role on the radar: a collapsed line you can scan, an expanded body you can act on.
 *
 * The collapsed line is the whole point of the tab. It has to answer "is this role moving, and if
 * not where is it stuck" without being opened, which is why the four phase dots sit in the header
 * next to the overall pill rather than inside the body — the dots ARE the radar, the body is the
 * evidence for them.
 *
 * ⚠️ The header is a plain `<button>`, not the design-system `Button`, and that is deliberate. It is
 * a full-width disclosure ROW carrying a heading, chips, a dot strip and a status pill — an
 * accessible click target wrapped around a layout, not a labelled action. `Button` brings its own
 * padding, intrinsic width, height and centred label, none of which a row like this can accept. The
 * cockpit uses plain `<button>` for every disclosure of this shape (`checkin/RoleTable.tsx`,
 * `rolePrioritisation/PortfolioOverview.tsx`, `tracking/trackingUi.tsx`). The tab's two real actions
 * — Retry and Re-read — DO use the atom, matching `tracking/TrackingTab.tsx`.
 */
import type { ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../../../shared';
import type { RadarPosition } from '../../../types/taBottleneck';
import type { RoleEvaluation, Stage } from '../../../utils/bottleneckRadar';
import type { RadarAction } from '../../../utils/radarActions';
import { Dot, PriorityChip, RampChip, SignalRow, StatusPill } from './radarUi';
import { RecommendedActions } from './RecommendedActions';
import { STATUS } from './radarStatus';
import type { AvailabilityRound, AvailabilityStage, RoleAvailability } from '../../../types/interviewAvailability';
import { AvailabilityDot, AvailabilityTrafficLight } from '../../../components/interviewAvailability/AvailabilityLight';
import {
  basisLabel,
  describeStage,
  isShown,
  shortLabel,
  withMoveLabel,
} from '../../../components/interviewAvailability/availabilityCopy';

/**
 * One availability row inside the Case & Final phase card. Deliberately NOT a `SignalRow`: those
 * feed the phase's own traffic light, and availability must not — a phase converting well is not
 * off because next week's calendars are full, and folding the two together would hide which one
 * is the problem.
 */
function AvailabilityRow({
  stage,
  round,
  windowEnd,
}: {
  stage: AvailabilityStage | null;
  round: AvailabilityRound;
  windowEnd: string;
}) {
  if (!isShown(stage)) return null;
  const move = withMoveLabel(stage);
  const basis = basisLabel(stage);
  return (
    <div className="flex items-start justify-between gap-2 text-[11.5px]" title={describeStage(stage, round, windowEnd)}>
      <span className="flex items-center gap-1.5 text-asphalt">
        <AvailabilityDot stage={stage} className="h-2 w-2" />
        {round} slots, next 7 days
      </span>
      <span className="text-right tabular-nums text-black">
        {shortLabel(stage, round)}
        {move ? <span className="block text-[10.5px] text-steel">{move}</span> : null}
        {stage?.panelSource === 'kickoff' ? (
          <span className="block text-[10.5px] text-steel">panel from kick-off sheet</span>
        ) : null}
        {stage?.partial ? <span className="block text-[10.5px] text-steel">at least — a link was unreadable</span> : null}
        {basis ? <span className="block text-[10.5px] text-steel">{basis}</span> : null}
      </span>
    </div>
  );
}

/** A phase's own card inside the expanded body. Tinted only when it is THE bottleneck. */
function PhaseCard({
  stage,
  isBottleneck,
  children,
}: {
  stage: Stage;
  isBottleneck: boolean;
  /** Extra rows under the signals — the availability light, on the Case & Final card only. */
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-solid border-pearl p-3',
        isBottleneck ? STATUS[stage.status].pill.split(' ')[0] : 'bg-snow'
      )}
    >
      <div className="mb-2.5 flex items-center gap-2">
        <Dot status={stage.status} />
        <span className="text-[11.5px] font-semibold text-black">{stage.name}</span>
      </div>
      <div className="flex flex-col gap-2.5">
        {stage.signals.map((s) => (
          <SignalRow key={s.label} {...s} />
        ))}
        {children ? <div className="flex flex-col gap-2 border-t border-solid border-pearl pt-2">{children}</div> : null}
      </div>
    </div>
  );
}

export function RoleRadarCard({
  position,
  evaluation,
  actions,
  expanded,
  onToggle,
  hiresInView,
  availability = null,
}: {
  position: RadarPosition;
  evaluation: RoleEvaluation;
  /** Prioritised next steps for this role, already capped. Empty for an unmeasured posting. */
  actions: RadarAction[];
  expanded: boolean;
  onToggle: () => void;
  /**
   * How many of this posting's open hires the active filter selected. Passed only when that is
   * FEWER than all of them, which happens when two clusters or departments share the posting.
   * Without it the card would say "8 open hires" under a strip totalling 3, with nothing to
   * explain the gap — the same unexplained discrepancy that made "22 open roles" untrustworthy.
   * The light itself is still judged on all 8: the pipeline is shared, so its coverage is too.
   */
  hiresInView?: number;
  /** Case and Final availability over today + 7 days; null while loading or unavailable. */
  availability?: RoleAvailability | null;
}) {
  const { stages, overall, bottleneck, ramp } = evaluation;
  const where = [position.location, position.team].filter(Boolean).join(' · ');

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-card">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-snow"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 flex-none text-iron" />
        ) : (
          <ChevronRight className="h-4 w-4 flex-none text-iron" />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-black">{position.title}</span>
            {position.rating ? <PriorityChip rating={position.rating} /> : null}
            {where ? <span className="text-xs text-steel">{where}</span> : null}
          </div>
          <div className="mt-1 text-[11.5px] text-iron">
            <span className="tabular-nums">
              {position.openHires} open {position.openHires === 1 ? 'hire' : 'hires'}
              {hiresInView != null && hiresInView !== position.openHires
                ? ` (${hiresInView} in this view)`
                : ''}
            </span>
            {position.weeksOpen != null ? (
              <span className="tabular-nums"> · {position.weeksOpen} wks open</span>
            ) : null}
            {!position.measured ? (
              <span> · no longer published in Lever — nothing to measure</span>
            ) : bottleneck ? (
              <>
                {' · '}
                <span className="text-asphalt">
                  {bottleneck.status === 'red' ? 'Bottleneck: ' : 'Watch: '}
                  <strong className={cn('font-semibold', STATUS[bottleneck.status].text)}>
                    {bottleneck.name}
                  </strong>
                </span>
              </>
            ) : null}
          </div>
        </div>

        {stages.length ? (
          // Titled, so the two traffic lights side by side say which question each answers:
          // where the role is stuck (here), and whether there is time to interview (beside it).
          <span className="flex flex-none flex-col items-center gap-1">
            <span className="text-[10px] font-semibold text-steel">Bottleneck</span>
            <span className="flex gap-1.5" aria-hidden>
              {stages.map((s) => (
                <span key={s.key} title={`${s.name}: ${STATUS[s.status].label}`} className="flex">
                  <Dot status={s.status} size="lg" />
                </span>
              ))}
            </span>
          </span>
        ) : null}
        {availability ? (
          <AvailabilityTrafficLight
            rounds={[
              { round: 'First', stage: availability.first },
              { round: 'Personal', stage: availability.personal },
              { round: 'Case', stage: availability.case },
              { round: 'Final', stage: availability.final },
            ]}
            windowEnd={availability.windowEnd}
          />
        ) : null}
        {ramp && position.weeksOpen != null ? <RampChip weeks={position.weeksOpen} /> : null}
        <StatusPill status={overall} />
      </button>

      {expanded ? (
        <div className="border-t border-solid border-pearl p-4">
          {stages.length ? (
            <>
              {/*
                The advice sits ABOVE the evidence. A reader opening a red card wants to know what
                to do about it; the four phase cards are why we say so, and they are still one
                glance away.
              */}
              {actions.length ? (
                <div className="mb-3">
                  <RecommendedActions actions={actions} ramp={ramp} />
                </div>
              ) : null}
              <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
                {stages.map((s) => (
                  <PhaseCard
                    key={s.key}
                    stage={s}
                    isBottleneck={bottleneck?.key === s.key && s.status !== 'unknown'}
                  >
                    {s.key === 's2' && availability && (isShown(availability.first) || isShown(availability.personal)) ? (
                      <>
                        <AvailabilityRow stage={availability.first} round="First" windowEnd={availability.windowEnd} />
                        <AvailabilityRow
                          stage={availability.personal}
                          round="Personal"
                          windowEnd={availability.windowEnd}
                        />
                      </>
                    ) : null}
                    {s.key === 's3' && availability && (isShown(availability.case) || isShown(availability.final)) ? (
                      <>
                        <AvailabilityRow stage={availability.case} round="Case" windowEnd={availability.windowEnd} />
                        <AvailabilityRow stage={availability.final} round="Final" windowEnd={availability.windowEnd} />
                      </>
                    ) : null}
                  </PhaseCard>
                ))}
              </div>
              {position.recruiters.length ? (
                <div className="mt-3 text-[11px] text-steel">
                  {position.recruiters.length === 1 ? 'Recruiter' : 'Recruiters'}:{' '}
                  {position.recruiters.join(', ')}
                  {position.cluster ? ` · ${position.cluster}` : ''}
                </div>
              ) : null}
            </>
          ) : (
            <p className="max-w-[76ch] text-sm text-asphalt">
              This requisition points at a Lever posting that is no longer published, so the
              warehouse has no funnel to read. Either the posting was closed while the requisition
              stayed open in the hiring plan, or the posting id on the row is wrong.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
